import type { AccountSave } from '../../game/account';
import { type CharacterSave, itemLevel } from '../../game/character';
import type { ZoneOpts } from '../../game/netHooks';
import { registry } from '../../sim/registry';
import type { ActivityKind, Tone } from './env';
import { type Raid, type SiegeMode, colossusClaimed, gatesCleared, nextGate, riftEnergy, siegeMode } from './lockouts';
import { COLOSSUS, CRUCIBLE, RIFT } from './rules';

/**
 * Everything the Instances panel lists: activities, their entries
 * (dungeon, colossus, difficulty), requirements, lockout status and the
 * matchmaking spec. Pure data + functions of the character save.
 */

export interface ActivityEntry {
  key: string;
  name: string;
  /** Short line under the name (boss, floor count…). */
  sub: string;
  minLevel: number;
  ilvl: number;
  /** Item level is a hard requirement (otherwise it is only recommended). */
  ilvlRequired: boolean;
  /** Extra zone options for this entry (difficulty…). */
  opts: ZoneOpts;
  /** Zone to enter right now (first uncleared gate), or null when locked out. */
  zone(c: CharacterSave, a: AccountSave): string | null;
}

export interface Activity {
  id: ActivityKind;
  name: string;
  tagline: string;
  description: string;
  players: string;
  hue: number;
  glyph: 'orb' | 'spiral' | 'claw' | 'shield' | 'star' | 'lance';
  entries: ActivityEntry[];
  /** Matchmaking (online only). */
  queue: { size: number; min: number; pvp?: boolean } | null;
  rules: string[];
  /** Item ids previewed as rewards. */
  rewards: string[];
}

const fixed =
  (zone: string) =>
  (): string =>
    zone;

function gateEntry(raid: Raid, mode: SiegeMode | null): ActivityEntry['zone'] {
  return (c, a) => {
    if (raid === 'siege' && mode) {
      const lock = siegeMode(c, a);
      if (lock && lock !== mode) return null;
    }
    const g = nextGate(c, a, raid);
    return g === null ? null : `${raid}_${g}`;
  };
}

const bossName = (id: string, fallback: string): string => registry.monsters.get(id)?.name ?? fallback;

export const ACTIVITIES: Activity[] = [
  {
    id: 'story',
    name: 'Story Dungeons',
    tagline: 'Shard-guarded depths of the main story',
    description: 'Descend into the lairs that hold the Lantern shards. Each ends in a boss; slaying it opens the way out and advances the story. The first clear pays the full hoard.',
    players: 'Solo or party of up to 4',
    hue: 0xe49955,
    glyph: 'lance',
    entries: [
      { key: 'crypt', name: 'Hollowroot Crypt', sub: 'Act I · the Grubling Chieftain', minLevel: 7, ilvl: 0, ilvlRequired: false, opts: {}, zone: fixed('crypt') },
      { key: 'tomb', name: 'Sandsworn Tomb', sub: 'Act II · the Sandbound Warden', minLevel: 17, ilvl: 0, ilvlRequired: false, opts: {}, zone: fixed('tomb') },
      { key: 'keep', name: 'Frostfang Keep', sub: 'Act III · the Frost Matriarch', minLevel: 27, ilvl: 0, ilvlRequired: false, opts: {}, zone: fixed('keep') },
    ],
    queue: null,
    rules: ['Revive at a checkpoint or with a Phoenix Plume', 'Clearing the boss opens the hidden exit', 'The first clear pays the full hoard'],
    rewards: ['skill_potion', 'potion_small', 'grenade_flame'],
  },
  {
    id: 'rift',
    name: 'Rift Delve',
    tagline: 'Purify the rift across three floors',
    description: `Cut through endless rift spawn to fill the purification gauge. A portal opens at ${RIFT.portalAt[0]}% and ${RIFT.portalAt[1]}%; the last floor holds rift cores and a guardian. Reach 100% before the ${RIFT.timeLimit / 60}-minute timer runs out.`,
    players: 'Solo or party of up to 4',
    hue: 0xb070ff,
    glyph: 'spiral',
    entries: [{ key: 'rift', name: 'Rift Delve', sub: '3 floors · 5-minute run', minLevel: 30, ilvl: 250, ilvlRequired: false, opts: {}, zone: fixed('rift_1') }],
    queue: null,
    rules: [
      `${RIFT.energyCost} Rift Energy per rewarded run (spent on entry)`,
      'Elites refund 40% of cooldowns; motes grant short boons',
      'Shattered cores empower you and release a mini-boss',
      'Rested ×2: 20 Rested points double the end chest',
      'Free revive at the floor entrance',
    ],
    rewards: ['aether_shard', 'ore_armor', 'ore_weapon', 'leap_crystal', 'rift_sigil'],
  },
  {
    id: 'colossus',
    name: 'Colossus Hunt',
    tagline: 'Bring down a colossus in its arena',
    description: `A single colossal beast with layered health, stagger checks, counters and breakable parts. You have ${COLOSSUS.timeLimit / 60} minutes and ${COLOSSUS.revives} revives shared by the whole party.`,
    players: 'Solo or party of up to 4',
    hue: 0xff9040,
    glyph: 'claw',
    entries: (
      [
        ['cinderhide', 'Cinderhide Behemoth', 250, false, 'Tier A · weak to water'],
        ['gloomfang', 'Gloomfang', 290, true, 'Tier B · weak to holy'],
        ['rimebound', 'Rimebound Wyrm', 330, true, 'Tier C · weak to fire'],
        ['dunestrider', 'Dunestrider', 370, true, 'Tier D · weak to earth'],
      ] as const
    ).map(([id, name, ilvl, req, sub]) => ({ key: `colossus:${id}`, name: bossName(id, name), sub, minLevel: 30, ilvl, ilvlRequired: req, opts: {}, zone: fixed(`colossus_${id}`) })),
    queue: { size: 4, min: 2 },
    rules: [`${COLOSSUS.timeLimit / 60}-minute limit — time out and the hunt fails`, `${COLOSSUS.revives} revives shared by the party`, 'One reward claim per day (Rested ×2); practice any time', 'First clear of each colossus pays bonus gold'],
    rewards: ['colossus_mark', 'leap_crystal', 'ore_armor', 'ore_weapon'],
  },
  {
    id: 'deepvault',
    name: 'The Sunken Reliquary',
    tagline: 'Deepvault — two gates beneath the tide',
    description: 'A weekly abyssal dungeon of two gated bosses, each with an enrage timer. There are no revives inside a gate: if everyone falls, the gate restarts at the resupply station.',
    players: 'Party of 1–4',
    hue: 0x40c0b0,
    glyph: 'shield',
    entries: [{ key: 'deepvault', name: 'The Sunken Reliquary', sub: `Gate I ${bossName('drowned_warden', 'Drowned Warden')} · Gate II ${bossName('tide_siren', 'Tide Siren')}`, minLevel: 30, ilvl: 300, ilvlRequired: true, opts: {}, zone: gateEntry('deepvault', null) }],
    queue: { size: 4, min: 2 },
    rules: ['Weekly: each gate pays once per character', 'Enrage, then a berserk wipe if the boss still stands', 'No revives inside a gate', 'Buy a Bonus Chest with gold after each gate'],
    rewards: ['vault_token', 'leap_crystal', 'gem_dmg_2'],
  },
  {
    id: 'siege',
    name: 'Warlord Siege',
    tagline: 'The Hollow Throne — a three-gate raid',
    description: "Storm the Hollow Throne: the twin hounds, Vhal'Zeroth himself and his phantom. Mechanics trigger at health-bar counts. Normal allows one revive per player per attempt; Hard has none, more health and extra mechanics.",
    players: 'Raid of up to 8',
    hue: 0xff4030,
    glyph: 'star',
    entries: (['normal', 'hard'] as const).map((mode) => ({
      key: `siege:${mode}`,
      name: mode === 'hard' ? 'Warlord Siege · Hard' : 'Warlord Siege · Normal',
      sub: mode === 'hard' ? '+40% health, one extra mechanic per gate, no revives' : 'One revive per player per attempt',
      minLevel: 30,
      ilvl: mode === 'hard' ? 370 : 340,
      ilvlRequired: true,
      opts: { diff: mode },
      zone: gateEntry('siege', mode),
    })),
    queue: { size: 8, min: 2 },
    rules: ['Weekly: gold per gate, once per character', 'Normal and Hard share the lockout; your first gate locks the week', 'Optional Bonus Chest after each gate (gold)', 'Gate progress carries between groups within the week'],
    rewards: ['warlord_relic', 'catalyst', 'forge_tome'],
  },
  {
    id: 'crucible',
    name: 'The Crucible',
    tagline: 'Three against three, last team standing',
    description: `Best-of-five elimination rounds with normalised stats and crowd-control safety nets. Win ${CRUCIBLE.roundsToWin} rounds to take the match. A side without players is filled with sparring knights for practice.`,
    players: '3v3 (also 1v1 / 2v2)',
    hue: 0xff6040,
    glyph: 'shield',
    entries: [{ key: 'crucible', name: 'The Crucible', sub: 'Elimination · best of five', minLevel: 20, ilvl: 0, ilvlRequired: false, opts: {}, zone: fixed('crucible') }],
    queue: { size: 6, min: 2, pvp: true },
    rules: ['Gear is normalised: skill and positioning decide', '7 s of debuff immunity after hard crowd control', 'Everyone respawns at the start of each round', `Crests: win ${CRUCIBLE.crests.win}, draw ${CRUCIBLE.crests.draw}, loss ${CRUCIBLE.crests.loss}`],
    rewards: ['arena_crest'],
  },
];

export const activityById = (id: string): Activity | undefined => ACTIVITIES.find((a) => a.id === id);

export interface EntryStatus {
  /** Zone the Enter button would load (null when locked out). */
  zone: string | null;
  canEnter: boolean;
  /** Why entry is blocked. */
  blocked: string | null;
  /** Item level below the recommendation (entry allowed). */
  underGeared: boolean;
  /** Lockout / progress lines. */
  lockout: { text: string; tone: Tone }[];
}

export function entryStatus(act: Activity, e: ActivityEntry, c: CharacterSave, a: AccountSave): EntryStatus {
  const ilvl = itemLevel(c);
  const zone = e.zone(c, a);
  const lockout: EntryStatus['lockout'] = [];
  let blocked: string | null = null;
  if (c.level < e.minLevel) blocked = `Requires level ${e.minLevel}`;
  else if (e.ilvlRequired && ilvl < e.ilvl) blocked = `Requires item level ${e.ilvl}`;
  switch (act.id) {
    case 'story': {
      const done = !!c.flags[`${e.key}_cleared`];
      lockout.push(done ? { text: 'Cleared — repeat clears pay a smaller chest', tone: 'green' } : { text: 'Not yet cleared', tone: 'gold' });
      break;
    }
    case 'rift': {
      const en = riftEnergy(c, a);
      lockout.push({ text: `Rift Energy ${en} / 100`, tone: en >= RIFT.energyCost ? 'violet' : 'red' });
      if (en < RIFT.energyCost) lockout.push({ text: 'No energy — practice runs only (no chest)', tone: 'grey' });
      if (c.daily.riftRested >= 20) lockout.push({ text: `Rested ${c.daily.riftRested} — next run pays ×2`, tone: 'gold' });
      break;
    }
    case 'colossus': {
      if (colossusClaimed(c, a)) lockout.push({ text: 'Claimed today — practice hunts only', tone: 'grey' });
      else lockout.push({ text: "Today's claim is available", tone: 'green' });
      if (c.daily.colossusRested >= 20) lockout.push({ text: `Rested ${c.daily.colossusRested} — next claim ×2`, tone: 'gold' });
      const id = e.key.split(':')[1] ?? '';
      if (!c.flags[`colossus_first_${id}`]) lockout.push({ text: `First clear bonus: ${COLOSSUS.firstClearGold[id] ?? 100} gold`, tone: 'gold' });
      break;
    }
    case 'deepvault':
    case 'siege': {
      const raid = act.id;
      const done = gatesCleared(c, a, raid);
      const total = raid === 'deepvault' ? 2 : 3;
      const cleared = ['I', 'II', 'III'].slice(0, total).filter((_, i) => done.includes(i + 1));
      lockout.push({ text: `Gates cleared this week: ${done.length} / ${total}${cleared.length ? ` (${cleared.join(', ')})` : ''}`, tone: done.length ? 'green' : 'grey' });
      if (raid === 'siege') {
        const lock = siegeMode(c, a);
        const mode = e.opts.diff === 'hard' ? 'hard' : 'normal';
        if (lock && lock !== mode) {
          blocked ??= `Locked to ${lock === 'hard' ? 'Hard' : 'Normal'} this week`;
          lockout.push({ text: `Your week is locked to ${lock === 'hard' ? 'Hard' : 'Normal'}`, tone: 'red' });
        }
      }
      if (zone === null && !blocked) blocked = 'Cleared this week';
      else if (zone) lockout.push({ text: `Next: Gate ${['', 'I', 'II', 'III'][Number(zone.slice(-1))] ?? ''}`, tone: 'blue' });
      break;
    }
    case 'crucible':
      lockout.push({ text: `Crests: ${c.inventory.stacks.arena_crest ?? 0}`, tone: 'gold' });
      lockout.push({ text: `Matches ${c.counters.crucibleMatches ?? 0} · wins ${c.counters.crucibleWins ?? 0}`, tone: 'grey' });
      break;
  }
  return { zone, canEnter: !blocked && zone !== null, blocked, underGeared: !e.ilvlRequired && e.ilvl > 0 && ilvl < e.ilvl, lockout };
}

/** Human label used for the ready check / queue. */
export function entryLabel(act: Activity, e: ActivityEntry, zone: string | null): string {
  if (act.id === 'deepvault' || act.id === 'siege') {
    const gate = zone ? Number(zone.slice(-1)) : 1;
    return `${e.name} — Gate ${['', 'I', 'II', 'III'][gate] ?? gate}`;
  }
  if (act.id === 'colossus') return `Colossus Hunt: ${e.name}`;
  return e.name;
}

/** A fresh run id shared by every member of the group. */
export function newRunId(): string {
  return `run_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e9).toString(36)}`;
}
